import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GLOBAL_SEARCH_MIN_TERM_LENGTH,
  SearchableModule,
  useGlobalSearch,
} from '@sib/api';
import type { GlobalSearchItemDto } from '@sib/api';
import { useDebouncedValue } from '@sib/utils';
import { GLOBAL_SEARCH_DEBOUNCE_MS } from '../GlobalSearch.constants';
import { useRecentGlobalSearches } from '../GlobalSearch.store';

export const useGlobalSearchPanel = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const recentItems = useRecentGlobalSearches(state => state.items);
  const addItem = useRecentGlobalSearches(state => state.addItem);
  const debouncedSearchTerm = useDebouncedValue(
    searchTerm,
    GLOBAL_SEARCH_DEBOUNCE_MS
  );
  const trimmedInput = searchTerm.trim();
  const trimmedDebouncedTerm = debouncedSearchTerm.trim();
  const inputReady = trimmedInput.length >= GLOBAL_SEARCH_MIN_TERM_LENGTH;
  const canSearch =
    trimmedDebouncedTerm.length >= GLOBAL_SEARCH_MIN_TERM_LENGTH;
  const isDebouncing = inputReady && trimmedInput !== trimmedDebouncedTerm;

  const { data, isFetching, isError, isFetched } = useGlobalSearch(
    { searchTerm: trimmedDebouncedTerm },
    { enabled: canSearch }
  );

  const closeResults = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
  }, []);

  const openResults = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeTaskDetails = useCallback(() => {
    setSelectedTaskId(null);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setIsOpen(true);
  }, []);

  const handleItemSelect = useCallback(
    (item: GlobalSearchItemDto) => {
      addItem(item);
      closeResults();

      if (item.module === SearchableModule.Tasks) setSelectedTaskId(item.id);
    },
    [addItem, closeResults]
  );

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      )
        setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  return {
    containerRef,
    searchTerm,
    trimmedDebouncedTerm,
    recentItems,
    isOpen,
    selectedTaskId,
    showPreviousSearches: isOpen && trimmedInput.length === 0,
    showResults: isOpen && trimmedInput.length > 0,
    buckets: data?.buckets,
    isLoading: isDebouncing || isFetching || (canSearch && !isFetched),
    isError,
    showMinLengthHint: !inputReady,
    openResults,
    handleSearchChange,
    handleItemSelect,
    closeTaskDetails,
  };
};
